/**
 * Compact pill showing current pickup time in StickyTopBar.
 * Displays "ASAP" or formatted time.
 * Desktop: opens PickupTimePicker in a popover.
 * Mobile: opens PickupTimePicker in a bottom sheet.
 */

import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Clock } from 'lucide-react'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui'
import { PickupTimePicker } from '@/components/checkout'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setPickupInfo } from '@/store/orderSlice'
import { useSelectedLocation } from '@/hooks/useSelectedLocation'
import { useMenuData } from '@/hooks/useMenuData'
import { getEffectiveSchedule } from '@/utils/effectiveSchedule'

export function PickupTimePill() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const pickupInfo = useAppSelector((s) => s.order.pickupInfo)
  const orderType = useAppSelector((s) => s.order.orderType)
  const { location } = useSelectedLocation()
  const { restaurant } = useMenuData()
  const [sheetOpen, setSheetOpen] = useState(false)

  const schedule = useMemo(
    () => getEffectiveSchedule(location, restaurant),
    [location, restaurant]
  )

  // Only show for pickup orders
  if (orderType !== 'pickup') return null

  // Display label: "ASAP", today time bare ("10:00"), or tomorrow with localized prefix ("Tmrw 10:00")
  const rawPickup = pickupInfo?.pickupTime ?? ''
  const timeOnly = rawPickup.replace(/^(Today|Tomorrow), /, '')
  const displayLabel = rawPickup === 'ASAP'
    ? t('common:asSoonAsPossible')
    : rawPickup.startsWith('Tomorrow')
      ? `${t('common:tomorrowShort')} ${timeOnly}`
      : timeOnly || t('common:pickupTime')

  const pickerContent = (
    <PickupTimePicker
      value={pickupInfo}
      onChange={(selection) => dispatch(setPickupInfo(selection))}
      hours={schedule.hours}
      hoursExceptions={schedule.hoursExceptions}
      timeZone={schedule.timeZone}
      lastOrderMinutes={schedule.lastOrderMinutes}
    />
  )

  const pillStyles = "items-center gap-1.5 px-2.5 py-2 min-h-[44px] rounded-full border border-border bg-muted/50 hover:bg-muted text-foreground transition-colors cursor-pointer"

  return (
    <>
      {/* Desktop: popover */}
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className={`hidden sm:inline-flex text-sm ${pillStyles}`}>
            <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate max-w-[130px]">{displayLabel}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-[380px] p-4 max-h-[70vh] overflow-y-auto"
        >
          {pickerContent}
        </PopoverContent>
      </Popover>

      {/* Mobile: bottom sheet */}
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className={`inline-flex sm:hidden text-xs ${pillStyles}`}
      >
        <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate max-w-[100px]">{displayLabel}</span>
      </button>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto px-4 pb-8">
          <SheetHeader className="mb-4">
            <SheetTitle>{t('common:pickupTime')}</SheetTitle>
          </SheetHeader>
          {pickerContent}
        </SheetContent>
      </Sheet>
    </>
  )
}
